import React, { useEffect, useState } from 'react';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import type { User as OidcUser } from 'oidc-client-ts';
import { store } from '../store';
import { loginSuccess, logout } from '../store/slices/authSlice';
import { oidcConfig } from '../config/featureFlags';
import type { User } from '../types/Auth.types';
import { isLogoutInProgress, isOidcCallbackUrl } from '../utils/Auth.util';
import { resolvePlatformClaims } from '../utils/Claims.util';
import { PATHS } from '../constants/Paths.constants';

// Role and organization are resolved here, once, from the single platform_role
// claim, so the rest of the app can compare them by equality. Nothing is
// defaulted: claims that resolve to no role or no organization stay null and are
// handled by ProtectedRoute.
const claimsToUser = (profile: Record<string, any>): User => {
  const { role, orgSlug } = resolvePlatformClaims(profile.platform_role);

  return {
    id: profile.sub as string,
    email: (profile.email ?? profile.preferred_username ?? '') as string,
    name: (profile.name ?? profile.preferred_username ?? '') as string,
    role,
    orgSlug
  };
};

/**
 * Single UserManager instance shared across the whole app.
 * Using oidc-client-ts directly (without react-oidc-context's AuthProvider)
 * avoids the infinite re-render bug that occurs with React 19.
 */
export const userManager = new UserManager({
  authority: oidcConfig.authority,
  client_id: oidcConfig.clientId,
  redirect_uri: oidcConfig.redirectUri,
  post_logout_redirect_uri: oidcConfig.postLogoutRedirectUri,
  scope: oidcConfig.scope,
  response_type: 'code',
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.localStorage })
});

// ---- Redux sync via UserManager events (outside React render cycle) ----
// This is the key: event handlers run outside React, so they never cause
// the infinite re-render loops that AuthProvider/userManagerContext has.

userManager.events.addUserLoaded((user: OidcUser) => {
  if (isLogoutInProgress()) {
    return;
  }

  store.dispatch(loginSuccess(claimsToUser(user.profile)));
});

userManager.events.addUserUnloaded(() => {
  store.dispatch(logout());
});

const syncOidcUserToStore = (user: OidcUser | null) => {
  if (isLogoutInProgress()) {
    return;
  }

  if (user && !user.expired) {
    store.dispatch(loginSuccess(claimsToUser(user.profile)));
    return;
  }

  store.dispatch(logout());
};

userManager.events.addSilentRenewError((error) => {
  console.warn('[OIDC] Silent renew error:', error);
});

interface OidcAuthProviderProps {
  children: React.ReactNode;
}

/**
 * Thin wrapper — provides no extra React context, just renders children.
 * All OIDC logic (UserManager, Redux sync) lives at module level above.
 * The actual callback handling is in OidcCallbackPage (/auth/callback).
 */
export const OidcAuthProvider: React.FC<OidcAuthProviderProps> = ({
  children
}) => {
  // A pending callback must not be treated as "no session": getUser() returns
  // null until the code is redeemed, and dispatching logout here would send the
  // user straight back to Keycloak.
  const shouldSkipInit =
    window.location.pathname === PATHS.OIDC_CALLBACK ||
    window.location.pathname === PATHS.LOGOUT ||
    isOidcCallbackUrl();

  const [initialized, setInitialized] = useState(shouldSkipInit);

  useEffect(() => {
    if (shouldSkipInit) {
      return;
    }

    let active = true;

    const initializeOidcUser = async () => {
      try {
        const user = await userManager.getUser();
        syncOidcUserToStore(user);
      } catch (error) {
        console.error('[OIDC] Session restore error:', error);
        syncOidcUserToStore(null);
      } finally {
        if (active) {
          setInitialized(true);
        }
      }
    };

    void initializeOidcUser();

    return () => {
      active = false;
    };
  }, [shouldSkipInit]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-500">Signing in...</p>
      </div>
    );
  }

  return <>{children}</>;
};
